import { formatTaskToGanttTask } from '@blackhyve/utilities/gantt';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import EditPredecessorDialog from 'components/projectOverview/gantt/components/dialog/EditPredecessorDialog';
import { initGanttColumns } from 'components/projectOverview/gantt/ganttConfig/column/columnConfig';
import BulkEditMenu from 'components/projectOverview/gantt/ganttConfig/column/menu/BulkEditMenu';
import { drawFloatLayer } from 'components/projectOverview/gantt/ganttConfig/float';
import ganttStore from 'components/projectOverview/gantt/ganttConfig/ganttStore';
import { selectCurrentUserRole } from 'features/auth';
import { fetchTrades } from 'features/companies/store/companiesSlice';
import { selectAllDependencies } from 'features/dependencies/store/dependenciesSlice';
import PlanningBoardTaskDrawer from 'features/drawers/components/PlanningBoardTaskDrawer';
import Gantt from 'features/gantt/components/Gantt';
import { addForecastedDatesLayer } from 'features/gantt/layers/addForecastedDatesLayer';
import { addWeekdaysLayer } from 'features/gantt/layers/addWeekdaysLayer';
import { clearLocations, fetchLocations } from 'features/locations/store/locationsSlice';
import { fetchProjects, selectProjectEntities } from 'features/projects/store/projectsSlice';
import {
  clearTasks,
  fetchTasks,
  selectAllPlanningTasks,
  setPredecessorDialog,
} from 'features/tasks/store/tasksSlice';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import RecipeDrawer from '../../drawers/components/RecipeDrawer';
import { taskGanttDataProcessor } from '../utils/gantt/taskGanttDataProcessor';
import { taskGanttEvents, taskGanttInlineEvents } from '../utils/gantt/taskGanttEvents';
import { drawCompanyColorLayer } from '../utils/gantt/taskGanttLayers';
import initMakers from '../utils/gantt/taskGanttMarkers';
import { taskGanttRenderer } from '../utils/gantt/taskGanttRenderer';
import { templates } from '../utils/gantt/taskGanttTemplates';
import { zoomConfig } from '../utils/gantt/zoomConfig';

export const TaskPortfolioGantt = ({ ganttId, reloadGantt }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const gantt = ganttStore.createGantt(ganttId);
  const projectEntities = useSelector((state) => selectProjectEntities(state));
  const predecessorDialog = useSelector((state) => state.tasks.predecessorDialog);
  const userRole = useSelector((state) => selectCurrentUserRole(state));
  const tasks = useSelector((state) => selectAllPlanningTasks(state))
    .map((task) => formatTaskToGanttTask(task))
    .map((task) => {
      const project = projectEntities[task.project_id];
      task.readonly = !(
        userRole === 'super_admin' ||
        userRole === 'admin' ||
        (project?.user_access_level
          ? project?.user_access_level === 'full_access'
          : project?.general_access_level === 'full_access')
      );
      return task;
    });
  const dependencies = useSelector((state) => selectAllDependencies(state));

  const handleClosePredecessorDialog = () => {
    dispatch(setPredecessorDialog({ taskId: null, open: false }));
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dispatch(fetchProjects()),
      dispatch(fetchTasks({ undefined, includes: 'latestJobWalk' })),
      dispatch(fetchLocations()),
      dispatch(fetchTrades()),
    ]).then(() => {
      setLoading(false);
    });
    return () => {
      dispatch(clearTasks());
      dispatch(clearLocations());
      // dispatch(resetToDefaultView());
    };
  }, [dispatch]);

  const getGanttColumn = () => {
    const columnEntities = initGanttColumns(ganttId);
    return [
      columnEntities.action,
      columnEntities.wbs,
      columnEntities.name,
      columnEntities.uuid,
      columnEntities.project,
      columnEntities.status,
      columnEntities.duration,
      columnEntities.startDate,
      columnEntities.endDate,
      columnEntities.forecastedStartDate,
      columnEntities.forecastedEndDate,
      columnEntities.actualStartDate,
      columnEntities.actualEndDate,
      columnEntities.effortHours,
      columnEntities.predecessor,
      columnEntities.crewSize,
      columnEntities.location,
      columnEntities.zone,
      columnEntities.area,
      columnEntities.companies,
      columnEntities.contacts,
      columnEntities.responsible,
      columnEntities.trades,
      columnEntities.totalSlack,
      columnEntities.freeSlack,
      columnEntities.progress,
      columnEntities.pace,
      columnEntities.earned_days,
      columnEntities.earned_planned_days,
      columnEntities.pr,
    ];
  };

  return !reloadGantt && !loading && tasks && dependencies && gantt ? (
    <>
      <Gantt
        columns={getGanttColumn()}
        data={{ tasks, links: dependencies }}
        ganttId={ganttId}
        initDataProcessor={taskGanttDataProcessor}
        initMakers={initMakers}
        inlineEvents={taskGanttInlineEvents}
        layers={[addWeekdaysLayer, drawFloatLayer, drawCompanyColorLayer, addForecastedDatesLayer]}
        plugins={{ critical_path: true, grouping: true, tooltip: true }}
        templates={templates}
        typeRenders={{ task: taskGanttRenderer, project: taskGanttRenderer }}
        zoomConfig={zoomConfig}
        config={{
          highlight_critical_path: false,
          fit_tasks: false,
          auto_types: false,
          sort: true,
          placeholder_task: false,
          order_branch: false,
        }}
        events={[
          ...taskGanttEvents,
          {
            onParse: function () {
              this._sort = { name: 'start_date', direction: 'desc' };
              this.sort('start_date');
            },
          },
        ]}
      />
      <RecipeDrawer ganttId={ganttId} />
      {predecessorDialog.open && (
        <EditPredecessorDialog
          ganttId={ganttId}
          handleClose={handleClosePredecessorDialog}
          open={predecessorDialog.open}
          taskId={predecessorDialog.taskId}
        />
      )}
      <BulkEditMenu ganttId={ganttId} />
      <PlanningBoardTaskDrawer ganttId={ganttId} />
    </>
  ) : (
    <Grid
      container
      alignItems={'center'}
      direction={'column'}
      justifyContent={'center'}
      style={{ height: '100%' }}
    >
      <CircularProgress />
      <Typography>Please Wait...</Typography>
    </Grid>
  );
};
